import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globals, LogOut as sharedLogOut } from '../../globalfunctions.js';
import roleManager from '../../utils/roleManager';
import simpleSessionTimer from '../../utils/simpleSessionTimer';
import mfaEnforcer from '../../utils/mfaEnforcer';
import { useKeyboardOpen } from '../../hooks/mobile';
import { useTicketNotificationCount } from '../../hooks/useTicketNotificationCount';
import { TAB_PRIORITY, isDetailPath, isItemActive, pickTabs, titleForPath } from '../../utils/mobileNav';
import BottomSheet from './BottomSheet';
import useThemeMode, { THEME_ICONS, THEME_LABELS } from '../../hooks/useThemeMode';
import CommandPalette from '../CommandPalette';
import './MobileChrome.css';

const initialsOf = () => {
  const name = `${Globals.FirstName || ''} ${Globals.LastName || ''}`.trim() || Globals.UserName || '';
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
};

const logOut = () => {
  simpleSessionTimer.clearSession();
  mfaEnforcer.clear2FASession();
  sessionStorage.removeItem('sidebarExpanded');
  Globals.UserName = '';
  Globals.UserType = '';
  Globals.LoginTime = null;
  Globals.FirstName = '';
  Globals.LastName = '';
  sharedLogOut();
};

// Phone navigation for the staff app, built the way phone apps are:
//  - a top bar with the page title (a back arrow on detail pages), search and
//    your account;
//  - a bottom tab bar with the sections you use most, in thumb reach;
//  - a "More" sheet for everything else, and an account sheet.
// It replaces the old floating hamburger + slide-out drawer, whose open state
// covered the page and whose button sat on top of content.
export default function MobileChrome() {
  const navigate = useNavigate();
  const { mode: themeMode, cycle: cycleTheme } = useThemeMode();
  const location = useLocation();
  const keyboardOpen = useKeyboardOpen();
  const ticketCount = useTicketNotificationCount(location.pathname, true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const items = useMemo(
    () => [{ path: '/home', label: 'Home', icon: 'fas fa-house' }, ...roleManager.getVisibleNavigationItems()],
    []
  );
  const tabs = useMemo(() => pickTabs(items), [items]);
  const tabPaths = new Set(tabs.map((t) => t.path));
  const active = items.find((item) => isItemActive(item, location.pathname));
  const moreActive = !!active && !tabPaths.has(active.path);
  const title = titleForPath(location.pathname, items);
  const detail = isDetailPath(location.pathname);

  // Close sheets when the route changes (a link inside them was followed).
  useEffect(() => {
    setMoreOpen(false);
    setAccountOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // The page needs to know the chrome is there to leave room for it.
  useEffect(() => {
    document.body.classList.add('has-mobile-chrome');
    return () => document.body.classList.remove('has-mobile-chrome');
  }, []);
  useEffect(() => {
    document.body.classList.toggle('kb-open', keyboardOpen);
    return () => document.body.classList.remove('kb-open');
  }, [keyboardOpen]);

  const onTab = (tab) => {
    // Tapping the tab you're already on goes back to its top, like phone apps do.
    if (active && active.path === tab.path) {
      if (location.pathname.toLowerCase().replace(/\/+$/, '') === tab.path) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    navigate(tab.path);
  };

  const goBack = () => {
    // A deep link has no history to go back to: fall back to the section.
    if (window.history.length > 1) navigate(-1);
    else navigate(active ? active.path : '/home');
  };

  // Sections in the order people reach for them (the same priority that picks
  // the tabs), then everything else; one-off destinations share a "More" list at
  // the end instead of being scattered between the sections.
  const rank = (path) => {
    const i = TAB_PRIORITY.indexOf(path);
    return i === -1 ? TAB_PRIORITY.length : i;
  };
  const withSubmenu = items
    .filter((item) => item.submenu && item.submenu.length > 1)
    .sort((x, y) => rank(x.path) - rank(y.path))
    .map((item) => ({ heading: item.label, rows: item.submenu, isTicket: item.path === '/tickets' }));
  const singles = items
    .filter((item) => !(item.submenu && item.submenu.length > 1) && !tabPaths.has(item.path))
    .sort((x, y) => rank(x.path) - rank(y.path));
  const grouped = singles.length > 0 ? [...withSubmenu, { heading: 'More', rows: singles, isTicket: false }] : withSubmenu;

  const role = String(Globals.UserType || '').replace(/^\w/, (c) => c.toUpperCase());
  const fullName = `${Globals.FirstName || ''} ${Globals.LastName || ''}`.trim() || Globals.UserName;

  return (
    <>
      <header className="m-topbar">
        {detail ? (
          <button type="button" className="m-topbar-btn" onClick={goBack} aria-label="Back">
            <i className="fas fa-chevron-left" aria-hidden="true"></i>
          </button>
        ) : (
          <button type="button" className="m-topbar-logo" onClick={() => navigate('/home')} aria-label="Home">
            <img src="/logo192.png" alt="" width="32" height="32" />
          </button>
        )}
        <h1 className="m-topbar-title">{title}</h1>
        <button type="button" className="m-topbar-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
          <i className="fas fa-magnifying-glass" aria-hidden="true"></i>
        </button>
        <button type="button" className="m-topbar-btn m-avatar-btn" onClick={() => setAccountOpen(true)} aria-label="Account">
          <span className="m-avatar" aria-hidden="true">{initialsOf()}</span>
        </button>
      </header>

      <nav className="m-tabbar" aria-label="Main">
        {tabs.map((tab) => {
          const isActive = !!active && active.path === tab.path;
          return (
            <button
              key={tab.path}
              type="button"
              className={`m-tab ${isActive ? 'is-active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onTab(tab)}
            >
              <span className="m-tab-icon">
                <i className={tab.icon} aria-hidden="true"></i>
                {tab.path === '/tickets' && ticketCount > 0 && (
                  <span className="m-tab-badge" aria-label={`${ticketCount} ticket updates`}>{ticketCount > 99 ? '99+' : ticketCount}</span>
                )}
              </span>
              <span className="m-tab-label">{tab.label}</span>
            </button>
          );
        })}
        <button type="button" className={`m-tab ${moreActive || moreOpen ? 'is-active' : ''}`} onClick={() => setMoreOpen(true)} aria-haspopup="dialog">
          <span className="m-tab-icon"><i className="fas fa-ellipsis" aria-hidden="true"></i></span>
          <span className="m-tab-label">More</span>
        </button>
      </nav>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} variant="sheet" />

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Menu">
        {grouped.map((section) => (
          <div className="m-section" key={section.heading + section.rows[0].path}>
            <div className="m-section-heading">{section.heading}</div>
            {section.rows.map((row) => {
              const here = location.pathname.toLowerCase().replace(/\/+$/, '');
              const rowActive = here === row.path.toLowerCase() || (row.path !== '/' && here.startsWith(`${row.path.toLowerCase()}/`));
              return (
                <button key={row.path} type="button" className={`m-row ${rowActive ? 'is-active' : ''}`} onClick={() => navigate(row.path)}>
                  <span className="m-row-icon"><i className={row.icon} aria-hidden="true"></i></span>
                  <span className="m-row-label">{row.label}</span>
                  {section.isTicket && row.path === '/tickets' && ticketCount > 0 && <span className="m-row-badge">{ticketCount}</span>}
                  <i className="fas fa-chevron-right m-row-chevron" aria-hidden="true"></i>
                </button>
              );
            })}
          </div>
        ))}
        <div className="m-section">
          <button type="button" className="m-row m-row-danger" onClick={logOut}>
            <span className="m-row-icon"><i className="fas fa-right-from-bracket" aria-hidden="true"></i></span>
            <span className="m-row-label">Log out</span>
          </button>
        </div>
      </BottomSheet>

      <BottomSheet open={accountOpen} onClose={() => setAccountOpen(false)} label="Account">
        <div className="m-account">
          <span className="m-avatar m-avatar-lg" aria-hidden="true">{initialsOf()}</span>
          <div className="m-account-text">
            <div className="m-account-name">{fullName}</div>
            {role && <div className="m-account-role">{role}</div>}
          </div>
        </div>
        <div className="m-section">
          <button type="button" className="m-row" onClick={() => navigate('/settings')}>
            <span className="m-row-icon"><i className="fas fa-cog" aria-hidden="true"></i></span>
            <span className="m-row-label">Settings</span>
            <i className="fas fa-chevron-right m-row-chevron" aria-hidden="true"></i>
          </button>
          <button type="button" className="m-row" onClick={cycleTheme}>
            <span className="m-row-icon"><i className={`fas ${THEME_ICONS[themeMode]}`} aria-hidden="true"></i></span>
            <span className="m-row-label">Theme: {THEME_LABELS[themeMode]}</span>
          </button>
          <button type="button" className="m-row m-row-danger" onClick={logOut}>
            <span className="m-row-icon"><i className="fas fa-right-from-bracket" aria-hidden="true"></i></span>
            <span className="m-row-label">Log out</span>
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
